import { inject, Injectable } from "@angular/core";
import { PlanOption, PlanOptions } from "@vault/web-vault/app/models/types/subscription.type";
import { NativePurchases, PURCHASE_TYPE, Transaction } from "@capgo/native-purchases";
import { Purchases } from "@revenuecat/purchases-capacitor";
import { Capacitor } from "@capacitor/core";
import { SubscriptionConfigType } from "@vault/web-vault/app/models/types/environement-config.type";
import { DataRepositoryService } from "@vault/web-vault/app/services/DataRepository/data-repository.service";
import { ConfirmationDialogService } from "@vault/web-vault/app/services/confirmation/confirmation.service";
import { NotificationEnum } from "@vault/web-vault/app/models/enum/notification.enum";
import { SubscriptionHttpPollingService } from "@vault/web-vault/app/services/subscription/subscription-http-polling.service";
import { UserStoreService } from "@vault/web-vault/app/services/store/user/user.store.service";
const subscriptionGeneralEndPoint = process.env.SUBSCRIPTION as unknown as SubscriptionConfigType;
const REVENUECAT = process.env.REVENUECAT || "";

const appleProductIdMapping = {
  [PlanOptions.premiumAnnual.subscriptionPaymentCycle]: "GlossVaultpremium",
};

interface OfferCodePlugin {
  openRedemptionSheet(): Promise<void>;
}

// SKErrorPaymentCancelled surfaces as code 1 or 2, or a message containing 'cancel'.
export function isCancelledPurchaseError(e: any): boolean {
  return (
    e?.code === 1 ||
    e?.code === 2 ||
    String(e?.message ?? "")
      .toLowerCase()
      .includes("cancel")
  );
}

/**
 * A credit pack StoreKit can't sell: its product lookup failed (not in App Store Connect, a
 * network error, …). Thrown before the payment sheet, so nothing can have been charged (#6249).
 */
export class StoreKitProductUnavailableError extends Error {
  constructor(
    readonly productId: string,
    readonly lookupError: unknown,
  ) {
    super(
      `StoreKit product ${productId} unavailable: ${(lookupError as Error)?.message ?? String(lookupError)}`,
    );
    this.name = "StoreKitProductUnavailableError";
  }
}

@Injectable({
  providedIn: "root",
})
export class AppleConfigService {
  result = "";
  private dataRepositoryService = inject(DataRepositoryService);
  private notifyService: ConfirmationDialogService = inject(ConfirmationDialogService);
  private subscriptionService: SubscriptionHttpPollingService = inject(
    SubscriptionHttpPollingService,
  );
  private userStoreService: UserStoreService = inject(UserStoreService);
  iOSIAPCapability: boolean = Capacitor.getPlatform() === "ios";
  appleButtonAvailable: boolean = true;
  subscriptionConfig: Record<string, any>;
  private isRevenueCatReady: boolean = false;
  private appID: string = "prod.gloss.vault.app";

  constructor() {
    this.subscriptionConfig = subscriptionGeneralEndPoint;
    if (this.iOSIAPCapability) {
      this.revenueCatConfigReady().then(() => {
        this.isRevenueCatReady = true;
      });
    }
  }

  async revenueCatConfigReady() {
    try {
      await Purchases.configure({
        apiKey: REVENUECAT,
      });
    } catch (e) {
      this.notifyService.notify(NotificationEnum.error, "errorOccurred", e, false);
    }
  }

  async isStoreKitSupport() {
    const { isBillingSupported } = await NativePurchases.isBillingSupported();
    return isBillingSupported;
  }

  async purchaseItem(plan: PlanOption, userId: string) {
    try {
      if (!(await this.isStoreKitSupport())) {
        this.notifyService.notify(NotificationEnum.error, "errorOccurred", "storeKitNotSupport");
        return;
      }

      const transaction: Transaction = await NativePurchases.purchaseProduct({
        productIdentifier: appleProductIdMapping[plan.subscriptionPaymentCycle],
        productType: PURCHASE_TYPE.SUBS,
        appAccountToken: userId,
      });

      await this.subscriptionService.pollSubStatus(transaction?.transactionId);
    } catch (e: any) {
      if (isCancelledPurchaseError(e)) {
        return;
      }
      this.notifyService.notify(NotificationEnum.error, "errorOccurred", "purchaseFail");
    }
  }

  async purchaseCreditPack(
    productId: string,
    appAccountToken: string,
  ): Promise<Transaction | null> {
    // A failed support check is treated as unsupported, so every rejection below comes from the
    // purchase itself (callers treat those as possibly charged).
    if (!(await this.isStoreKitSupport().catch(() => false))) {
      this.notifyService.notify(NotificationEnum.error, "errorOccurred", "storeKitNotSupport");
      return null;
    }
    // The same lookup purchaseProduct starts with, done first so its failures - a productId
    // missing from App Store Connect, a network error - surface before the payment sheet as
    // StoreKitProductUnavailableError, which callers can show as failed rather than "processing"
    // (#6249). The plugin rejects every purchaseProduct error with only its message.
    try {
      await NativePurchases.getProduct({
        productIdentifier: productId,
        productType: PURCHASE_TYPE.INAPP,
      });
    } catch (e: unknown) {
      throw new StoreKitProductUnavailableError(productId, e);
    }
    try {
      return await NativePurchases.purchaseProduct({
        productIdentifier: productId,
        productType: PURCHASE_TYPE.INAPP,
        appAccountToken,
      });
    } catch (e: any) {
      if (isCancelledPurchaseError(e)) {
        return null;
      }
      // Its own lookup still failing (the product went away in between): also pre-sheet. Matched
      // on the plugin's message text, like isCancelledPurchaseError.
      if (String(e?.message).startsWith("Cannot find product")) {
        throw new StoreKitProductUnavailableError(productId, e);
      }
      throw e;
    }
  }

  async fetchCurActiveSubscriptions() {
    const endpoint = this.subscriptionConfig.endpoint["get_active_subscription"];
    const url = `${this.subscriptionConfig.url}/${this.subscriptionConfig.apiStage}`;
    return await this.dataRepositoryService.send("GET", `${endpoint}`, null, true, true, url);
  }

  verifyPurchaseAsActiveSub(sub: any, transactionId: string) {
    return sub.subscription_id === transactionId && sub.active === true;
  }

  async openRedemptionSheet() {
    try {
      this.finalCheckActiveSub();
      if (this.isRevenueCatReady) {
        await Purchases.presentCodeRedemptionSheet();
      }
    } catch (e) {
      this.notifyService.notify(NotificationEnum.error, "errorOccurred", e, false);
    }
  }

  async openPrefilledRedeemUrl(offerCode: string) {
    try {
      this.finalCheckActiveSub();
      window.open(`https://apps.apple.com/redeem?ctx=offercodes&id=6575383509&code=${offerCode}`);
    } catch (e) {
      this.notifyService.notify(NotificationEnum.error, "errorOccurred", e, false);
    }
  }

  private finalCheckActiveSub() {
    // Read live: this root service can be created before the subscription loads, and a copy
    // taken then would stay null (making this check a no-op) or go stale after a logout.
    const sub = this.userStoreService.subscription.activeSubscription();
    // Mirrors the points-redemption gate: free users and Apple subscribers may redeem.
    if (sub && sub.tier !== "free" && sub.paymentProvider !== "apple") {
      throw new Error("apple device only feature");
    }
  }

  async getPaymentHistory() {
    const purchaseHistories = await NativePurchases.getPurchases();
    const mappedHistories: { charges?: any[]; invoices?: any[] } | null = {
      charges: [],
      invoices: [],
    };
    const cachedProducts: Record<string, any> = {};

    for (const purchaseHistory of purchaseHistories.purchases) {
      const pId = purchaseHistory.productIdentifier;
      cachedProducts[pId] ??= (
        await NativePurchases.getProduct({
          productIdentifier: pId,
        })
      ).product;
      mappedHistories.charges.push({
        description: `Subscription ${purchaseHistory.transactionReason}`,
        amount: cachedProducts[pId]?.price,
        currency: cachedProducts[pId]?.currencyCode,
        status: "succeeded",
        paid: true,
        created: this.convertIsoDateToUnix(purchaseHistory?.purchaseDate),
      });
    }

    return mappedHistories;
  }

  convertIsoDateToUnix(isoDate: string) {
    return Math.floor(new Date(isoDate).getTime());
  }

  async getOfferCode() {
    const endpoint = this.subscriptionConfig.endpoint["get_offer_code"];
    const url = `${this.subscriptionConfig.url}/${this.subscriptionConfig.apiStage}`;
    return await this.dataRepositoryService.send("GET", `${endpoint}`, null, true, true, url);
  }

  async verifyOfferCodeTransaction(payload: any) {
    const endpoint = this.subscriptionConfig.endpoint["verify_offer_code_transaction"];
    const url = `${this.subscriptionConfig.url}/${this.subscriptionConfig.apiStage}`;
    return await this.dataRepositoryService.send(
      "POST",
      `${endpoint}`,
      { data: payload },
      true,
      true,
      url,
    );
  }
}
